import React from 'react'
import cx from 'classnames'
import {
  Calendar,
  HardDrive,
  ListChecks,
  Gift,
  Icon,
  EnvelopeOpen,
  Smiley,
  DeviceMobile,
  At,
} from '@phosphor-icons/react'
import { useRecoilValue } from 'recoil'
import { activeStepState } from '../../store/atoms/active-step'
import { useTranslation } from 'react-i18next'
import { NavLink, useParams } from 'react-router-dom'

type ProgressBarStep = {
  name: string
  url: string
  icon: Icon
  step: number
  hide?: boolean
  onClick?: () => void
}

export const stepRoutes = (
  step: number,
  id: string,
  referral?: boolean,
  reuseSameIncentives?: boolean,
) => {
  const routes = [
    id ? `/campaigns/details/${id}` : '#',
    id ? `/campaigns/questionnaire/${id}` : '#',
    id ? `/campaigns/referral-questionnaire/${id}` : '#',
    id ? `/campaigns/incentives/${id}` : '#',
    id ? `/campaigns/referral-incentives/${id}` : '#',
    id ? `/campaigns/compose-creative/${id}` : '#',
    id ? `/campaigns/emails/${id}` : '#',
    id ? `/campaigns/happiness-check/${id}` : '#',
    id ? `/campaigns/ec-flow/${id}/end-user/home` : '#',
    id ? `/campaigns/end-users/${id}` : '#',
  ]

  // Handle step routing based on campaign type
  if (referral) {
    if (reuseSameIncentives) {
      // Referral campaign with reused incentives: show referral questionnaire but skip referral incentives
      if (step <= 4) {
        // Steps 1-4 (up to Incentives) use normal routing
        return routes[step - 1]
      } else {
        // Steps 5+ skip referral incentives: step 5 (Compose creative) -> routes[5]
        return routes[step]
      }
    } else {
      // Referral campaign with separate incentives: show all steps including both incentive types
      if (step <= 5) {
        // Steps 1-5 use normal routing (no skipping needed)
        return routes[step - 1]
      } else {
        // Steps 6+ continue with normal routing
        return routes[step - 1]
      }
    }
  } else {
    // Non-referral campaign: skip steps 3 (referral questionnaire) and 5 (referral incentives)
    // For non-referral campaigns, we have 8 total steps
    switch (step) {
      case 1: // Campaign details
        return routes[0]
      case 2: // Questionnaire
        return routes[1]
      case 3: // Skip referral questionnaire (step 3) and go to incentives
        return routes[3] // Incentives
      case 4: // Compose creative (skip referral incentives)
        return routes[5]
      case 5: // Emails
        return routes[6]
      case 6: // Happiness check
        return routes[7]
      case 7: // Finalise
        return routes[8]
      case 8: // Data upload
        return routes[9]
      default:
        return '#' // Fallback for invalid step
    }
  }
}

export const ProgressBar: React.FC<{
  completedStep: number
  showReferralQuestionnaire: boolean
  hasReferralIncentives: boolean
  referral?: boolean
  reuseSameIncentives?: boolean
}> = ({
  completedStep,
  showReferralQuestionnaire,
  hasReferralIncentives,
  referral,
  reuseSameIncentives,
}) => {
    const { t } = useTranslation()
    const { id } = useParams()
    const activeStep = useRecoilValue(activeStepState)
    // completedStep counts only the steps this campaign type has; shift it onto the 10-step numbering.
    const skippedSteps = referral ? (reuseSameIncentives ? 1 : 0) : 2
    const completedStepOffset = completedStep + 1 + skippedSteps

    // Keep original step numbers for logic/routing, add uiIndex for display
    const steps: (ProgressBarStep & { uiIndex: number })[] = [
      {
        name: t('progress_bar.campaign_details'),
        url: id && 1 <= completedStepOffset ? `/campaigns/details/${id}` : '#',
        icon: Calendar,
        step: 1,
      },
      {
        name: t('progress_bar.questionnaire'),
        url:
          id && 2 <= completedStepOffset ? `/campaigns/questionnaire/${id}` : '#',
        icon: ListChecks,
        step: 2,
      },
      {
        name: t('progress_bar.referral_questionnaire'),
        url:
          id && 3 <= completedStepOffset
            ? `/campaigns/referral-questionnaire/${id}`
            : '#',
        icon: ListChecks,
        step: 3,
        hide: !showReferralQuestionnaire,
      },
      {
        name: t('progress_bar.incentive'),
        url: id && 4 <= completedStepOffset ? `/campaigns/incentives/${id}` : '#',
        icon: Gift,
        step: 4,
      },
      {
        name: t('progress_bar.referral_incentive'),
        url:
          id && 5 <= completedStepOffset
            ? `/campaigns/referral-incentives/${id}`
            : '#',
        icon: Gift,
        step: 5,
        hide: !hasReferralIncentives || !referral || reuseSameIncentives,
      },
      {
        name: t('progress_bar.compose_creative'),
        url:
          id && 6 <= completedStepOffset
            ? `/campaigns/compose-creative/${id}`
            : '#',
        icon: EnvelopeOpen,
        step: 6,
      },
      {
        name: t('progress_bar.emails'),
        url: id && 7 <= completedStepOffset ? `/campaigns/emails/${id}` : '#',
        icon: At,
        step: 7,
      },
      {
        name: t('progress_bar.happiness_check'),
        url:
          id && 8 <= completedStepOffset
            ? `/campaigns/happiness-check/${id}`
            : '#',
        icon: Smiley,
        step: 8,
      },
      {
        name: t('progress_bar.finalise'),
        url:
          id && 9 <= completedStepOffset
            ? `/campaigns/ec-flow/${id}/end-user/home`
            : '#',
        icon: DeviceMobile,
        step: 9,
      },
      {
        name: t('progress_bar.data_upload'),
        url: id && 10 <= completedStepOffset ? `/campaigns/end-users/${id}` : '#',
        icon: HardDrive,
        step: 10,
      },
    ]
      .filter((step) => !step.hide)
      .map((step, idx) => ({ ...step, uiIndex: idx + 1 })) // uiIndex for display only

    const isCompleted = (step: ProgressBarStep) => {
      const mappedActiveStep = getMappedActiveStep(activeStep)
      const mappedCompletedStep = getMappedActiveStep(completedStep)
      return step.step <= mappedActiveStep || step.step <= mappedCompletedStep
    }

    const isCurrent = (step: ProgressBarStep) => {
      // Use the helper function to get the mapped active step
      const mappedActiveStep = getMappedActiveStep(activeStep)

      const current = step.step === mappedActiveStep

      return current
    }
    // Helper function to map active step for non-referral campaigns
    const getMappedActiveStep = (step: number): number => {
      if (!referral) {
        // Map the active step from the component to the correct logical step in the progress bar
        switch (step) {
          case 3:
            return 4 // Incentives
          case 4:
            return 6 // Compose creative
          case 5:
            return 7 // Emails
          case 6:
            return 8 // Happiness check
          case 7:
            return 9 // Finalise
          case 8:
            return 10 // Data upload
          default:
            return step
        }
      }
      // Referral reusing incentives has no Referral incentives (5): its 5+ are one further.
      if (reuseSameIncentives && step >= 5) {
        return step + 1
      }
      return step
    }

    const isUpcomming = (step: ProgressBarStep) => {
      const mappedActiveStep = getMappedActiveStep(activeStep)
      const mappedCompletedStep = getMappedActiveStep(completedStep)
      return step.step > mappedActiveStep && step.step > mappedCompletedStep
    }

    return (
      <nav aria-label="Progress" className="min-w-fit">
        <ol role="list" className="overflow-hidden">
          {steps.map((step, stepIdx) => {
            return (
              <li
                key={step.name}
                className={cx(
                  { 'pb-5': stepIdx !== steps.length - 1 },
                  'relative',
                )}
              >
                <NavLink to={step.url}>
                  {stepIdx !== steps.length - 1 && (
                    <div
                      className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                      aria-hidden="true"
                    />
                  )}
                  <div
                    className={'group relative flex items-center cursor-pointer'}
                  >
                    <span className="flex h-9 items-center">
                      <span
                        className={cx(
                          'relative z-10 flex h-8 w-8 items-center justify-center rounded-full',
                          {
                            'border-2': isCurrent(step) || isCompleted(step),
                          },
                          { 'bg-accent': isCompleted(step) },
                          { 'border-gray-300': isUpcomming(step) },
                          {
                            '!border-accent': isCompleted(step) || isCurrent(step),
                          },
                          { border: isUpcomming(step) },
                          { '!border-accent': isCurrent(step) },
                          {
                            'bg-white': isCurrent(step) || isUpcomming(step),
                          },
                        )}
                      >
                        <step.icon
                          size={32}
                          weight="fill"
                          color={
                            isUpcomming(step)
                              ? 'var(--gray-300)'
                              : 'var(--color-black-100)'
                          }
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </span>
                    </span>
                    <span className="ml-4 flex min-w-0 flex-col">
                      <span className="text-sm font-medium">{step.name}</span>
                    </span>
                  </div>
                </NavLink>
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
